import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { compose, branch, renderComponent } from 'recompose';
import { components as clientComponents } from 'mage-client';
import { AppError, capabilityUtils } from 'moet-core';

import { merchantRegion } from '@/selectors/merchant';

/**
 *
 * @param {*} mapCapabilitiesToProps An object that contains a map of the
 *  capabilities to be called
 * @param {Object} options
 * @param {string[]} options.forceCapabilitiesOrder Enforce order of execution.
 *  If not specified, relies on the unreliable JS ordering from Object.keys
 * @param {({payload, responses: [], props}) => any} options.callback Will be called
 *  after all promises are resolved
 */
export const withChainedCapabilitiesBeforeNext = (
  mapCapabilitiesToProps,
  { forceCapabilitiesOrder = false, callback = () => null } = {},
) => {
  // capabilitiesToRun is an array with all the capabilities names that we need to run.
  // The Capabilites received as arguments are turned into promises.
  const capabilitiesToRun = forceCapabilitiesOrder || Object.keys(mapCapabilitiesToProps);

  return function withCapabilitiesOnNextInner(WrappedComponent) {
    const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
    const wrappedDisplayName = `WithCapabilitiesOnNext(${displayName})`;

    class WithCapabilitiesOnNext extends React.Component {
      constructor(props) {
        super(props);
        const { defaultState = {} } = props;
        this.state = {
          isLoading: false,
          isDisabled: false,
          defaultState,
        };

        this.handleNext = this.handleNext.bind(this);
        this.resetState = this.resetState.bind(this);
      }

      handleNext({
        payload: passedPayload = {},
        hasChanged = true,
        scrollBeforeNext = false,
        toPassNext,
        onNextCallback,
      }) {
        const { onNext, merchantRegion: region } = this.props;

        if (!hasChanged) {
          return onNext(toPassNext);
        }
        this.setState({
          error: null,
          isLoading: true,
          isDisabled: true,
        });

        const payload =
          typeof passedPayload === 'object' && passedPayload !== null
            ? { ...passedPayload, region }
            : { region };

        // Execute all the capabilities in order received, chaining the result
        // and returning all the results to the "then" function.
        // This is also sending the same payload to all the requests
        return capabilitiesToRun
          .reduce(
            (capChain, currentCapName) =>
              capChain.then(capChainResults =>
                // eslint-disable-next-line promise/no-nesting
                this.props[currentCapName](payload).then(currentCapResult => [
                  ...capChainResults,
                  currentCapResult,
                ]),
              ),
            Promise.resolve([]),
          )
          .then(responses => {
            try {
              // eslint-disable-next-line
              callback({ payload, responses, props: this.props });
            } catch (e) {
              console.error(e);
            }
            return null;
          })
          .then(() => {
            if (scrollBeforeNext) {
              window.scrollTo({ top: 0, left: 0 });
            }

            if (onNextCallback) {
              onNextCallback();
            }

            onNext(toPassNext);
            return null;
          })
          .catch(error => {
            this.setState({ isDisabled: false, isLoading: false, error });
            return error;
          });
      }

      resetState() {
        this.setState({
          error: null,
          isLoading: false,
          isDisabled: false,
        });
      }

      render() {
        const {
          isDisabled: propsIsDisabled = false,
          isLoading: propsIsLoading = false,
          ...props
        } = this.props;
        const { error = null, isDisabled = false, isLoading = false } = this.state;
        return (
          <WrappedComponent
            {...props}
            error={error}
            isDisabled={propsIsDisabled || isDisabled}
            isLoading={propsIsLoading || isLoading}
            onNext={this.handleNext}
            resetState={this.resetState}
          />
        );
      }
    }

    WithCapabilitiesOnNext.displayName = wrappedDisplayName;
    WithCapabilitiesOnNext.WrappedComponent = WrappedComponent;
    WithCapabilitiesOnNext.propTypes = {
      onNext: PropTypes.func.isRequired,
      defaultState: PropTypes.object,
      isDisabled: PropTypes.bool,
      isLoading: PropTypes.bool,
      merchantRegion: PropTypes.string,
    };

    return compose(
      connect(state => ({ merchantRegion: merchantRegion(state) })),
      // This will turn all capabilities "names" into promises and inject into the
      // WithCapabilitiesOnNext component
      clientComponents.withPromiseBasedCapabilities(mapCapabilitiesToProps),
      // In case the user has no access to one of the capabilities, we render access denied
      branch(
        props => {
          // Create an object with capabilities only ( e.g. { capName: capFunc })
          const capabilitiesObject = capabilitiesToRun.reduce(
            (capObject, currCapName) => ({ ...capObject, [currCapName]: props[currCapName] }),
            {},
          );

          return !capabilityUtils.hasCapsAccess(capabilitiesObject, props);
        },
        renderComponent(() => <AppError defaultTemplate="forbidden" hasGoBack={false} />),
      ),
    )(WithCapabilitiesOnNext);
  };
};
